import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { TmtLoggerService } from 'tmt-logger';

import { Classification, ClassificationReason } from 'src/enums/classification';
import { TestRunStatus } from 'src/enums/testRunStatus';
import { environment } from 'src/environments/environment';
import { TargetEnvironment } from 'src/environments/environment.interfaces';
import { OverviewItem } from 'src/models/overview';
import { FPC } from 'src/models/sops';
import { TestResult } from 'src/models/testResult';
import { TroubleReport } from 'src/models/troubleReport';
import { GetTestRunAction } from 'src/services/Actions/TestRunAction';
import { FileService } from 'src/services/file.service';
import { TestRunBaseService } from 'src/services/test-run.base.service';
import { TestRunStatistics, TestRunViewModel } from 'src/models/testRun';
import { TestRunService } from 'src/services/test-run.service';
import { DialogComponent } from 'src/modules/test-results-page/components/dialog/dialog.component';
import { HistoryComponent } from 'src/modules/test-results-page/components/history/history.component';

export interface NotExecutedType {
	name: string;
	value: string;
}
@Component({
	selector: 'app-test-run',
	templateUrl: './test-run.component.html',
	styleUrls: ['./test-run.component.scss'],
	providers: [{ provide: FileService }],
})
export class TestRunComponent implements OnInit {
	view: any[] = [650, 350];

	single: any[];

	colorScheme = {
		domain: ['#438151', '#D6001C', '#16417F', '#FA6E23', '#CEB888', '#82848A', '#A6D1AD'],
	};

	public xcomFiles$ = this.fileService.xcomFiles$;

	public atsFiles$ = this.fileService.atsFiles$;

	public e2Files$ = this.fileService.e2Files$;

	public offboardFiles$ = this.fileService.offboardFiles$;

	constructor(
		private testRunBaseService: TestRunBaseService,
		private testRunService: TestRunService,
		private route: ActivatedRoute,
		public dialog: MatDialog,
		private router: Router,
		private testRunActionService: GetTestRunAction,
		private loggerService: TmtLoggerService,
		private fileService: FileService,
		private destroyRef: DestroyRef,
	) {
		if (this.environment !== TargetEnvironment.Hero)
			this.displayedColumns = [
				'testCaseItemNo',
				'testCaseName',
				'result',
				'troubleReports',
				'resultComment',
				'failedReasonText',
				'failedBy',
				'failedReasonComment',
				'testCaseAlternativeFailedReasons',
			];
		else this.displayedColumns = ['testCaseItemNo', 'testCaseName', 'result', 'troubleReports', 'resultComment', 'failedReasonText', 'failedBy', 'failedReasonComment'];
	}

	testRun: TestRunViewModel;

	executionDateUTCString: string;

	displayedColumns: string[] = [];

	// TODO show Classification and ClassificationComment when we have data
	//  displayedColumns: string[] = ['TestCaseName', 'Result', 'TroubleReports','ResultComment', 'Classification', 'ClassificationComment',
	//  'FailedReasonText', 'FailedBy', 'FailedReasonComment'];
	dataSource;

	maxall = 10;

	@ViewChild(MatPaginator) paginator: MatPaginator;

	@ViewChild(MatSort) sort: MatSort;

	// TODO Classification is not yet saved on test result or in history table
	parentTypeID = 92; // TestResult

	fieldName = 'Classification';

	private subscription: Subscription;

	includesNotExecuted: boolean;

	countPassed: number = 0;

	countFailed: number = 0;

	countNotExecutedBySystem: number = 0;

	countNotExecutedByUser: number = 0;

	countOther: number = 0;

	private statisticsSubscription: Subscription;

	testRunStatistics: TestRunStatistics;

	notExecutedTypes: NotExecutedType[] = [
		{ name: 'FailedBySystem', value: 'N/A by system' },
		{ name: 'FailedByUser', value: 'N/A / N/P by user.' },
	];

	testResultStatistics: OverviewItem[] = [{ name: '', value: 0 }];

	troubleReportsInStatistics: TroubleReport[] = [{ name: '', uri: '', testResultUid: '', validFrom: new Date(), validTo: new Date() }];

	uniqueTroubleReportNamesInStatistics: string[];

	failedResults: TestResult[] = [<TestResult>{}];

	failedResultsWithoutTroubleReport: TestResult[] = [<TestResult>{}];

	originEcuReadout: string;

	fileTimeEcuReadout: Date;

	totalEcuReadouts: number;

	totalEcuParams: number;

	/** True when readout/param data came from the database (migrated file import time is misleading). */
	ecuReadoutsFromDatabase = false;

	ecuParamsFromDatabase = false;

	totalRunProperties: number;

	totalSOPS: number;

	totalOffboardComponents: number;

	originSOPS: string;

	fileTimeSOPS: Date;

	versionSOPS: number;

	FPCsSOPS: FPC[];

	classificationDescription: string;

	classificationReasonDescription: string;

	testRunStatusDescription: string;

	reportsurl = environment.reportsurl;

	fenixReportsUrl = environment.fenixReportsUrl;

	gettingTestRuns: boolean;

	resultsExist: boolean = true;

	tcAlternativeFailedReasonsExist: boolean = true;

	environment = environment.environment;

	TargetEnvironment = TargetEnvironment;

	ngOnInit() {
		this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(parmas => this.testRunActionService.getTestRun(parmas.uid));
		this.getTestRun();
	}

	private getTestRun() {
		this.resultsExist = true;
		this.gettingTestRuns = true;
		this.testRunActionService.testrunData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
			testrundata => {
				const testResults = testrundata?.testRun?.testResults ?? [];
				if (testResults.length === 0) {
					this.resultsExist = false;
				} else {
					this.fileService.initFilesInformationForTestRun(testrundata.testRun.testRunUid);
					this.testRun = testrundata;
					const d = new Date(testrundata.testRun.executionTime);
					this.executionDateUTCString = new Date(d.setHours(0, 0, 0, 0)).toISOString();
					this.classificationDescription = Classification[testrundata.testRun.classification];
					this.classificationReasonDescription = ClassificationReason[testrundata.testRun.classificationReason];
					this.testRunStatusDescription = TestRunStatus[testrundata.testRun.status];
					this.includesNotExecuted = testResults.some(x => x.failedReason !== 0);
					this.tcAlternativeFailedReasonsExist = testResults.some(x => x.testCaseAlternativeFailedReasons?.length > 0);
					this.dataSource = new MatTableDataSource<TestResult>(testResults);
					this.dataSource.paginator = this.paginator;
					this.dataSource.sortingDataAccessor = (item, property) => {
						switch (property) {
							case 'testCaseItemNo':
								return Number(item.testCaseItemNo?.split('-').pop() ?? 0);
							case 'testCaseName':
								return item.testCaseName;
							case 'result':
								return item.result;
							case 'troubleReports':
								return item.troubleReports?.length ?? 0;
							case 'resultComment':
								return item.resultComment;
							case 'failedReasonText':
								return item.failedReasonText;
							case 'failedBy':
								return item.failedBy;
							case 'failedReasonComment':
								return item.failedReasonComment;
							default:
								return item[property];
						}
					};
					this.dataSource.sort = this.sort;
					this.testRunService.selectedRunUid.next(testrundata.testRun.testRunUid);
					this.distributeResultsToStatistics(this.testRun);
					this.getFailedWithoutTroubleReports();
					this.getTestRunStatistics();
				}
				this.gettingTestRuns = false;
			},
			error => {
				this.loggerService.logError(error);
				this.gettingTestRuns = false;
			},
		);
	}

	public onChangeNotExecutedType(name: string, isChecked: boolean) {
		if (isChecked) {
			this.testRun.testRun.testResults.forEach(result => {
				if (name === 'FailedBySystem' && result.failedReason !== 0 && result.failedBy === 'System') {
					// System
					result.hide = false;
				} else if (name === 'FailedByUser' && result.failedReason !== 0 && result.failedBy !== 'System') {
					// User
					result.hide = false;
				}
			});
		} else {
			this.testRun.testRun.testResults.forEach(result => {
				if (name === 'FailedBySystem' && result.failedReason !== 0 && result.failedBy === 'System') {
					// System
					result.hide = true;
				} else if (name === 'FailedByUser' && result.failedReason !== 0 && result.failedBy !== 'System') {
					// User
					result.hide = true;
				}
			});
		}
	}

	public openHistoryDialog(parentUidIn): void {
		this.dialog.open(HistoryComponent, {
			width: '800px',
			data: { parentUid: parentUidIn, parentTypeID: this.parentTypeID, fieldName: this.fieldName },
		});
	}

	public openDialog(header, text): void {
		this.dialog.open(DialogComponent, {
			width: '800px',
			data: { header: header, text: text },
		});
	}

	public getPageSizeOptions(): number[] {
		if (this.dataSource && this.dataSource.paginator && this.dataSource.paginator.length > this.maxall) {
			return [10, 20, this.dataSource.paginator.length];
		} else {
			return [10, 20];
		}
	}

	public applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();
	}

	public navigateTo(row: any) {
		this.router.navigate(['/testresult/' + row.testResultUid]);
	}

	private distributeResultsToStatistics(run: TestRunViewModel) {
		this.troubleReportsInStatistics.length = 0;
		this.failedResults.length = 0;
		this.failedResultsWithoutTroubleReport.length = 0;
		this.countPassed = 0;
		this.countFailed = 0;
		this.countNotExecutedBySystem = 0;
		this.countNotExecutedByUser = 0;
		this.countOther = 0;

		run.testRun.testResults.forEach(res => {
			if (res.result === 'passed') {
				this.countPassed += 1;
			} else if (res.result === 'failed') {
				this.countFailed += 1;
				this.failedResults.push(res);
			} else if (res.failedReason === 1 && res.failedBy === 'System') {
				this.countNotExecutedBySystem += 1;
			} else if (res.failedBy === 'User') {
				this.countNotExecutedByUser += 1;
			} else {
				this.countOther += 1;
			}

			if (res.troubleReports?.length > 0) {
				res.troubleReports.forEach(tr => {
					this.troubleReportsInStatistics.push(tr);
				});
			}
		});

		this.uniqueTroubleReportNamesInStatistics = [...new Set(this.troubleReportsInStatistics.map(tr => tr.name))];

		this.testResultStatistics = [
			{ name: 'Passed' + ' (' + this.countPassed + ')', value: this.countPassed },
			{ name: 'Failed' + ' (' + this.countFailed + ')', value: this.countFailed },
			{ name: 'Not executed by System' + ' (' + this.countNotExecutedBySystem + ')', value: this.countNotExecutedBySystem },
			{ name: 'Not executed by User' + ' (' + this.countNotExecutedByUser + ')', value: this.countNotExecutedByUser },
			{ name: 'Other' + ' (' + this.countOther + ')', value: this.countOther },
		];
	}

	private getFailedWithoutTroubleReports() {
		this.failedResults.forEach(failedRes => {
			if (!failedRes.troubleReports?.length) {
				this.failedResultsWithoutTroubleReport.push(failedRes);
			}
		});
	}

	public onSelect(data): void {
		// console.log('Item clicked', JSON.parse(JSON.stringify(data)));
	}

	public onActivate(data): void {
		// console.log('Activate', JSON.parse(JSON.stringify(data)));
	}

	public onDeactivate(data): void {
		// console.log('Deactivate', JSON.parse(JSON.stringify(data)));
	}

	private getTestRunStatistics() {
		this.statisticsSubscription = this.route.params
			.pipe(
				switchMap(params => this.testRunBaseService.getTestRunStatistics(params.uid)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(
				teststatisticsdata => {
					this.testRunStatistics = teststatisticsdata;
				},
				error => this.loggerService.logError(error),
			);
	}

	public showEcuReadoutCounts(count: number): void {
		this.totalEcuReadouts = count;
	}

	public onReadoutsFromDatabase(fromDatabase: boolean): void {
		this.ecuReadoutsFromDatabase = fromDatabase;
	}

	public onParamsFromDatabase(fromDatabase: boolean): void {
		this.ecuParamsFromDatabase = fromDatabase;
	}

	public showEcuReadoutOrigin(originText: string): void {
		this.originEcuReadout = originText;
	}

	public showEcuReadoutFileTime(fileTime: Date): void {
		this.fileTimeEcuReadout = fileTime;
	}

	public showEcuParamCounts(count: number): void {
		this.totalEcuParams = count;
	}

	public showSOPSCounts(count: number): void {
		this.totalSOPS = count;
	}

	public showSOPSOrigin(originText: string): void {
		this.originSOPS = originText;
	}

	public showSOPSFileTime(fileTime: Date): void {
		this.fileTimeSOPS = fileTime;
	}

	public showSOPSVersion(version: number): void {
		this.versionSOPS = version;
	}

	public showSOPSFPCs(fpcs: FPC[]): void {
		this.FPCsSOPS = fpcs;
	}

	public showRunPropertiesCount(count: number): void {
		this.totalRunProperties = count;
	}

	public showOffboardComponentsCount(count: number): void {
		this.totalOffboardComponents = count;
	}
}
